import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Hotel, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useDisruptionProgress, useTriggerAllocate } from '@/lib/disruptions/use-disruptions';
import { BulkInProgressError, type DisruptionActiveRun } from '@/lib/disruptions/types';
import { operatorLabel } from './labels';
import { RebookingProgressDialog } from './rebooking-progress-dialog';

// Disruption-wide hotel-allocation trigger — the home of "Allocate hotels" (it
// moved out of the roster bulk bar so the Hotels tab is its single home). One BFF
// call across the WHOLE event; the domain owns the per-case/per-airport fan-out
// and the disruption-scoped lock (a re-run never disturbs confirmed reservations).
//
// S16 lock: only one bulk run per disruption at a time, across BOTH ops. We
// disable while a run is active (from the progress poll's `activeRun`, which may
// be a rebooking run started elsewhere, or optimistically from our last 409) and,
// on a 409, name the holder. The lock auto-releases when the run goes terminal.

export function EventAllocationActionBar({ disruptionUrn }: { disruptionUrn: string }) {
  const { t } = useTranslation('disruptions');
  const [progressOpen, setProgressOpen] = useState(false);
  const allocate = useTriggerAllocate(disruptionUrn);
  const progress = useDisruptionProgress(disruptionUrn);
  const polledRun: DisruptionActiveRun | null = progress.data?.activeRun ?? null;

  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const [conflict, setConflict] = useState<{ operatorUrn: string } | null>(null);

  // The poll is authoritative: once no run is active, clear any stale 409 banner.
  useEffect(() => {
    if (!polledRun) setConflict(null);
  }, [polledRun]);

  const holderUrn = polledRun?.operatorUrn ?? conflict?.operatorUrn ?? null;
  const locked = holderUrn !== null;
  const disabled = allocate.isPending || locked;
  const hardError = !!allocate.error && !(allocate.error instanceof BulkInProgressError);

  const onAllocate = (): void => {
    allocate.mutate(undefined, {
      onSuccess: (res) => {
        setLastRunId(res.runId);
        setConflict(null);
        setProgressOpen(true);
      },
      onError: (err) => {
        if (err instanceof BulkInProgressError) setConflict({ operatorUrn: err.inProgressBy });
      },
    });
  };

  return (
    <>
      <div
        className="flex shrink-0 flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm"
        data-testid="event-allocation-bar"
      >
        <span className="text-muted-foreground">{t('allocationBar.headline')}</span>

        {locked ? (
          <span
            className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400"
            role="status"
            data-testid="event-allocation-locked"
          >
            <Lock className="h-3 w-3" aria-hidden />
            {t('allocationBar.running', { operator: operatorLabel(holderUrn!) })}
          </span>
        ) : hardError ? (
          <span className="text-xs text-destructive" data-testid="event-allocation-error">
            {t('allocationBar.error')}
          </span>
        ) : lastRunId ? (
          <span className="text-xs text-muted-foreground" data-testid="event-allocation-run">
            {t('allocationBar.started')}{' '}
            <span className="font-mono" title={lastRunId}>
              {shortRunId(lastRunId)}
            </span>
          </span>
        ) : null}

        {conflict ? (
          <span className="sr-only" role="alert" data-testid="event-allocation-toast">
            {t('allocationBar.conflict', { operator: operatorLabel(conflict.operatorUrn) })}
          </span>
        ) : null}

        <div className="ml-auto">
          <Button size="sm" onClick={onAllocate} disabled={disabled} data-testid="event-allocate">
            {allocate.isPending ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <Hotel className="mr-1 h-3 w-3" aria-hidden />
            )}
            {t('allocationBar.action')}
          </Button>
        </div>
      </div>

      <RebookingProgressDialog
        disruptionUrn={disruptionUrn}
        open={progressOpen}
        onOpenChange={setProgressOpen}
        op="allocation"
      />
    </>
  );
}

/** runId is a secondary identifier — show a short head, full value on hover (§18). */
function shortRunId(runId: string): string {
  return runId.length > 12 ? `${runId.slice(0, 12)}…` : runId;
}
